<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import moment from "moment"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { SOURCES_TYPES } from "@core/constants/source"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { usePvPageShell } from "@modules/pv/composables/usePvPageShell"
import { formatIdSource } from "@core/helpers"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            meterLabel: "Compteur",
            createStudy: "Créer une étude PV",
            addPdl: "Ajouter un compteur électrique",
            launchEco: "Lancer une étude économique",
            noNameStudy: "Étude sans nom",
            ecoMenuHistoryTitle: "Historique des études photovoltaïques",
            at: "Réalisé le ",
        },
    },
})

// ================================
// EMITS
// ================================
const emit = defineEmits<{
    "click:create-dimensionnement": []
    "click:add-pdl": []
    "click:modal-etude-eco": [analyseId: string]
}>()

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const shell = usePvPageShell()
const pvStore = usePvStore()
const buildingStore = useBuildingStore()
const { getMeters, isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

const ecoMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)

// ================================
// DONNÉES CALCULÉES
// ================================
/** Options PDL pour le sélecteur */
const getPdlOptions = computed(() => {
    return getMeters.value
        .filter((s) => s.type === SOURCES_TYPES.PDL)
        .map((s) => ({
            label: s.name || formatIdSource(s.idsource, SOURCES_TYPES.PDL),
            value: s._id_source,
        }))
})

const getShowMeterSelect = computed(() => getPdlOptions.value.length > 1)

/**
 * Clé bâtiment + PDL (défaut) une fois les compteurs prêts : charge
 * `GET …/data/source/…/pv` pour aligner `lastExtrapolationAnalyseId` / le formulaire
 * dimensionnement (bloc extrapolation) sur toutes les sous-routes PV, pas seulement
 * l’onglet consommation.
 */
const getHeaderSourcePvLoadKey = computed((): string | null => {
    const bid = shell.getBuildingId.value
    if (!bid) return null
    if (isFetchingBuilding.value || isFetchingBuildingSources.value) return null
    const sid = pvStore.getResolvedSourceIdForCurrentBuilding()
    if (!sid) return null
    return `${bid}:${sid}`
})

/**
 * Renseigne `sourcePvData` + `lastExtrapolationAnalyseId` (présence d’une extrapolation).
 * `getResolvedSourceIdForCurrentBuilding` (DEV-378) garantit qu'on n'utilise
 * pas un `sourceId` hérité d'un autre bâtiment via navigation SPA.
 */
async function loadSourcePvDataForExtrapolationContext(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return
    if (isFetchingBuilding.value || isFetchingBuildingSources.value) return
    const sid = pvStore.getResolvedSourceIdForCurrentBuilding()
    if (!sid) return
    if (pvStore.getSourceId !== sid) {
        pvStore.setSourceId(sid, { fetchSourcePv: false })
    }
    await pvStore.fetchSourcePvData({ sourceId: sid, swallowError: true })
}

watch(
    getHeaderSourcePvLoadKey,
    (key) => {
        if (!key) return
        void loadSourcePvDataForExtrapolationContext()
    },
    { immediate: true }
)

const getEtudePvHistoryList = computed(() => pvStore.getEtudePvHistory ?? [])

const getEtudePvHistoryCount = computed(
    () => getEtudePvHistoryList.value.length
)

/** Entrée feuille du menu éco (slot `#item` : nom + date dessous). */
type EcoMenuPvLeaf = {
    label: string
    pvDateLabel: string | null
    command: () => void
}

/** Modèle menu : groupe avec titre au-dessus des études PV (PrimeVue `submenulabel`). */
const getEcoMenuItems = computed(() => {
    const items: EcoMenuPvLeaf[] = getEtudePvHistoryList.value.map((row) => {
        const id = String((row as { _id?: string })._id ?? "")
        const name = (row as { name_analyse?: string }).name_analyse
        const createdAt = (row as { createdAt?: string }).createdAt
        const studyName =
            typeof name === "string" && name.length > 0
                ? name
                : t("noNameStudy")
        const pvDateLabel = getEtudePvMenuDateLabel(createdAt)
        return {
            label: studyName,
            pvDateLabel,
            command: () => {
                if (id) emit("click:modal-etude-eco", id)
            },
        }
    })
    return [
        {
            label: t("ecoMenuHistoryTitle"),
            class: "text-sm",
            items,
        },
    ]
})

const getSingleMeterLabel = computed(() => {
    const first = getPdlOptions.value[0]
    return first?.label ?? ""
})

/** Dernière étude de dimensionnement (ligne d’info sur la route éco), aligné legacy */
const getLastDimStudyName = computed(() => {
    if (route.name !== "PageBuildingPVEtudeEconomique") return null
    if (!pvStore.getSourceId) return null
    const fromEco = pvStore.getLastEtudeEcoDimName
    if (typeof fromEco === "string" && fromEco.length > 0) {
        return fromEco
    }
    const rows = pvStore.getEtudePvHistory
    const fromHistory =
        Array.isArray(rows) && rows.length > 0
            ? (rows[0] as { name_analyse?: string }).name_analyse
            : undefined
    if (typeof fromHistory === "string" && fromHistory.length > 0) {
        return fromHistory
    }
    return null
})

// ================================
// MÉTHODES
// ================================

/**
 * Affichage menu : `DD/MM/YYYY à HH:mm` (24 h), ou `null` si date absente / invalide.
 */
function getEtudePvMenuDateLabel(
    createdAt: string | undefined | null
): string | null {
    if (typeof createdAt !== "string" || createdAt.length === 0) return null
    const m = moment(createdAt)
    if (!m.isValid()) return null
    return m.format("DD/MM/YYYY [à] HH:mm")
}

function onMeterChange(value: string | null): void {
    pvStore.setSourceId(value)
}

function onToggleEcoMenu(event: Event): void {
    ecoMenuRef.value?.toggle(event)
}

/** Date secondaire pour le slot menu (champ métier `pvDateLabel`). */
function getPvMenuItemDateLabel(item: {
    pvDateLabel?: string | null
}): string | null {
    const d = item.pvDateLabel
    return typeof d === "string" && d.length > 0 ? d : null
}
</script>

<template>
    <div class="header-pv-building w-full flex flex-col gap-3">
        <div class="flex flex-wrap items-center gap-2">
            <template v-if="getEtudePvHistoryCount > 0">
                <BaseButton
                    :label="t('launchEco')"
                    icon="pi pi-chevron-down"
                    icon-pos="right"
                    outlined
                    size="small"
                    @click="onToggleEcoMenu"
                />
                <BaseMenu ref="ecoMenuRef" :model="getEcoMenuItems" popup>
                    <template #item="{ item, props }">
                        <a
                            v-bind="props.action"
                            class="flex flex-col items-start! gap-0.5"
                        >
                            <span>{{ item.label }}</span>
                            <span
                                v-if="getPvMenuItemDateLabel(item)"
                                class="text-xs text-surface-600"
                            >
                                {{ t("at") }} {{ getPvMenuItemDateLabel(item) }}
                            </span>
                        </a>
                    </template>
                </BaseMenu>
            </template>
            <BaseButton
                v-if="pvStore.getSourceId"
                :label="t('createStudy')"
                icon="pi pi-plus"
                size="small"
                @click="emit('click:create-dimensionnement')"
            />
            <BaseButton
                v-if="getPdlOptions.length === 0"
                :label="t('addPdl')"
                icon="pi pi-plus"
                size="small"
                @click="emit('click:add-pdl')"
            />
        </div>
        <div
            v-if="getPdlOptions.length > 0"
            class="flex flex-wrap items-center gap-2 text-sm text-primary-900"
        >
            <BaseSelect
                v-if="getShowMeterSelect && pvStore.getSourceId"
                class="min-w-48"
                :options="getPdlOptions"
                option-label="label"
                option-value="value"
                :model-value="pvStore.getSourceId"
                :placeholder="t('meterLabel')"
                @update:model-value="onMeterChange"
            />
            <span v-else-if="pvStore.getSourceId">
                {{ t("meterLabel") }} : {{ getSingleMeterLabel }}
            </span>
            <template v-if="getLastDimStudyName">
                <span aria-hidden="true">•</span>
                <span>{{ getLastDimStudyName }}</span>
            </template>
        </div>
    </div>
</template>
